import useGet from '../../../core/composables/api/useGet'
import usePost from '../../../core/composables/api/usePost'
import { computed, ref, toValue, watch, type MaybeRef } from 'vue'
import type { FeedbackTopicType, SkillVisualization } from '@/core/models/feedback-topic'
import type { Perspective } from '@/core/models/perspective'
import { useDialog } from '@/core/components/base/dialog/use-dialog'
import type { EvaluationAnalysis, SurveyPerspectiveSummary } from '@/core/models/surveys-analytics'
import { useRoute } from 'vue-router'
import { useMembersStore } from '@/stores/members.store'

export const useCoacheeEvaluationProfile = (feedbackTopicId: MaybeRef<string>) => {
  const route = useRoute()

  const coacheeId = computed(() => route.params.id as string)
  const endpoint = computed(
    () => `coaching/coachee/${coacheeId.value}/profile/evaluation/${toValue(feedbackTopicId)}`,
  )
  const analysisEndpoint = computed(
    () =>
      `coaching/coachee/${coacheeId.value}/profile/evaluation/${toValue(feedbackTopicId)}/analysis`,
  )

  const { data, status, load, reload } = useGet<EvaluationProfile>(endpoint)

  const selectedSurveyIds = ref<string[]>([])

  const {
    data: analysisData,
    status: analysisStatus,
    mutate: loadAnalysis,
  } = usePost<EvaluationAnalysis, { surveyIds: string[] }>(analysisEndpoint.value)

  watch(
    endpoint,
    async () => {
      await load()
      if (status.value === 'success') {
        const allSurveyIds = data.value?.surveys.map((survey) => survey.id) ?? []
        selectedSurveyIds.value = allSurveyIds
        loadAnalysis({ surveyIds: allSurveyIds })
      }
    },
    { immediate: true },
  )

  const { openDialog, isOpen, closeDialog } = useDialog()

  const membersStore = useMembersStore()
  const coachee = computed(() => membersStore.getMemberById(coacheeId.value))

  const applySelectedSurveys = (surveyIds: string[]) => {
    closeDialog()
    selectedSurveyIds.value = [...surveyIds]
    loadAnalysis({ surveyIds: selectedSurveyIds.value })
  }

  return {
    data,
    status,
    coachee,
    load,
    reload,
    analysisData,
    analysisStatus,
    applySelectedSurveys,
    openDialog,
    isOpen,
    closeDialog,
    selectedSurveyIds,
  }
}

export interface EvaluationProfile {
  readonly title: string
  readonly id: string
  readonly type: FeedbackTopicType
  readonly icon?: string
  readonly perspectives: ReadonlyArray<Perspective>
  readonly includeSelf: boolean
  readonly showBlindspots: boolean
  readonly skillVisualization: ReadonlyArray<SkillVisualization>
  readonly surveys: ReadonlyArray<SurveySelectionPreview>
}

export interface SurveySelectionPreview {
  readonly id: string
  readonly title?: string
  readonly updatedAt: string
  readonly isFinished: boolean
  readonly perspectives: ReadonlyArray<SurveyPerspectiveSummary>
  readonly numberOfQuestions: number
}
